import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import {
	getFiles,
	exportFileFtp,
	patchAssertionSetToFile,
	checkExportedFiles,
	mergeFilesFtp,
	mergeFiles,
	postExportEdildeltaErp,
	postExportFaterErp,
} from 'entities/Files/api';
import { getCompanyUsersInfo } from 'entities/User/api';
import { GetFilesReq } from 'entities/Files/api/types';
import { getAssertionsSets } from 'entities/Assertions/api';
import { PageHeader } from 'shared/ui/PageHeader';
import { FileType, AlreadyExportedFilesType, EReviewIssue } from 'entities/Files/types';
import { SearchParams, useFilesSearchParams } from 'shared/helpers/useFilesSearchParams';
import { useFileSelection } from 'pages/Files/hooks/useFileSelection';
import { DeleteFilesModal } from 'widgets/files/DeleteFilesModal';
import { FilesDashboard } from 'widgets/files/FilesDashboard';
import { FiltersAndSearch } from 'widgets/files/FiltersAndSearch';
import { AsModalList } from 'widgets/Extensions/Details/ui/AssertionSets/ui/AsModalList';
import DownloadModal from 'pages/Files/partials/download-modal';
import RequiresReviewModal from 'pages/Files/partials/requires-review-modal';
import AlreadyExportedFiles from 'pages/Files/partials/already-exported-files';
import { useFilesStore } from 'widgets/editFile/model/state';
import { useCustomModelStore } from 'widgets/customModel/state';
import { ReadableCategories } from 'widgets/files/Header/consts/ReadableCategories';
import { FtpImportFilesModal } from 'widgets/importFiles/importFilesFromFtp/ui/FtpImportFilesModal';
import { useAssertionSetsStore } from 'widgets/Extensions/Details/ui/AssertionSets/model/state';
import { AxiosError } from 'axios';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { Prefixes } from 'app/config/routes/consts';
import { useAuthStore } from 'processes/auth/model/state';
import { downloadFile } from 'shared/helpers/downloadFile';
import { DefaultExportDestination, DefaultExportType } from 'entities/Company/types';
import { ProcessFile } from 'widgets/files/FilesDashboard/consts/DashboardColumns';
import { Loader } from 'shared/ui/Loader';
import { PageWrapper } from 'pages/_layouts/PageWrapper';

export const Files: FC = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { category, partnerId } = useParams();
	const { company } = useAuthStore();
	const { files, updateFilesData, setFilePartnerId, setFileDetails, page, setPage } = useFilesStore();
	const { setCustomFileDetails } = useCustomModelStore();
	const { assertionsSets, setAssertionsSetsData } = useAssertionSetsStore();
	const { searchParams, changeSearchParams, initialSearch } = useFilesSearchParams();
	const { data: companyUsersData } = useQuery({
		queryKey: ['get/users/company-users'],
		queryFn: getCompanyUsersInfo,
	});
	const uploaderOptions = useMemo(
		() =>
			companyUsersData?.data
				? companyUsersData.data.map((u: { _id: string; firstName: string; lastName: string }) => ({
						value: u._id,
						label: `${u.firstName} ${u.lastName}`,
				  }))
				: [],
		[companyUsersData?.data],
	);
	const [openFtpModal, setOpenFtpModal] = useState(false);
	const [downloadModalIsOpen, setDownloadModalIsOpen] = useState(false);
	const [requiresReviewModalOpen, setRequiresReviewModalOpen] = useState(false);
	const [reviewParams, setReviewParams] = useState<EReviewIssue[]>([]);
	const [alreadyExportedFilesModalOpen, setAlreadyExportedFilesModalOpen] = useState(false);
	const [fileType, setFileType] = useState<DefaultExportType>();
	const [fileIdForAction, setFileIdForAction] = useState('');
	const {
		selectedFiles,
		isAllSelected,
		isIndeterminate,
		handleToggleFile,
		handleToggleAllFiles,
		resetSelection,
	} = useFileSelection(files ?? []);
	const [deleteFilesOpen, setDeleteFilesOpen] = useState(false);
	const [filesCounters, setFilesCounters] = useState<any>(0);
	const [isAssertionsOpen, setIsAssertionsOpen] = useState(false);
	const [triggerExport, setTriggerExport] = useState(false);
	const [alreadyExportedFiles, setAlreadyExportedFiles] = useState<AlreadyExportedFilesType[]>();
	const [fileProcess, setFileProcess] = useState<ProcessFile>();

	const defaultAsertionId = useMemo(() => {
		if (fileIdForAction) {
			const file = files?.find((item) => item._id === fileIdForAction);
			return assertionsSets?.find((item) => item._id === file?.assertionSetsId)?._id;
		}
	}, [selectedFiles]);

	const fileIdsforActions = useMemo(
		() => (fileIdForAction ? [fileIdForAction] : selectedFiles),
		[fileIdForAction, selectedFiles],
	);

	const searchParamsForQuery: GetFilesReq = useMemo(() => {
		const {
			uploadSource,
			uploaderIdsFilter,
			importFrom,
			importTo,
			autoRemoveFrom,
			autoRemoveTo,
			...rest
		} = searchParams;
		const preparedSearchParamsForQuery: GetFilesReq = { ...rest, perPage: '20' };
		if (uploadSource !== 'all') preparedSearchParamsForQuery['uploadSource'] = uploadSource;
		if (uploaderIdsFilter?.length) preparedSearchParamsForQuery['uploaderIdsFilter'] = uploaderIdsFilter;
		if (importFrom) preparedSearchParamsForQuery['importFrom'] = importFrom;
		if (importTo) preparedSearchParamsForQuery['importTo'] = importTo;
		if (autoRemoveFrom) preparedSearchParamsForQuery['autoRemoveFrom'] = autoRemoveFrom;
		if (autoRemoveTo) preparedSearchParamsForQuery['autoRemoveTo'] = autoRemoveTo;

		return preparedSearchParamsForQuery;
	}, [searchParams]);

	const preparedFilesData = useMemo(() => {
		const preparedFiles: Record<string, Array<FileType>> = {};
		files?.forEach((file) => {
			const date = format(new Date(file.createdAt), 'dd/MM/yyyy');
			preparedFiles[date] = [...(preparedFiles[date] ?? []), { ...file }];
		});
		return preparedFiles;
	}, [files]);

	const { mutate: ftpExportMutate, isLoading: ftpExportIsLoading } = useMutation({
		mutationKey: ['post/files/export/ftp-export'],
		mutationFn: exportFileFtp,
	});

	const { mutate: mergeFtpMutate, isLoading: mergeFtpIsLoading } = useMutation({
		mutationKey: ['post/files/export/ftp-merge-multiple'],
		mutationFn: mergeFilesFtp,
	});

	const { mutate: mergeMutate } = useMutation({
		mutationKey: ['post/files/export/merge-multiple'],
		mutationFn: mergeFiles,
	});

	const { mutate: checkExportMutate } = useMutation({
		mutationKey: ['post/files/export/check-export'],
		mutationFn: checkExportedFiles,
	});

	const { mutate: exportEdildeltaErp } = useMutation({
		mutationKey: ['/files/export/edildelta-erp'],
		mutationFn: postExportEdildeltaErp,
		onSuccess: () => {
			enqueueSnackbar({ message: t('shared_texts.successful_export'), variant: 'success' });
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate: exportFaterErp } = useMutation({
		mutationKey: ['/files/export/fater-erp'],
		mutationFn: postExportFaterErp,
		onSuccess: () => {
			enqueueSnackbar({ message: t('shared_texts.successful_export'), variant: 'success' });
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	const { mutate: fileAssertionMutate, isLoading: fileAssertionLoading } = useMutation({
		mutationKey: ['patch/files/assertion-sets'],
		mutationFn: patchAssertionSetToFile,
	});

	const { data: filesResponse, isLoading } = useQuery({
		queryKey: ['get/files', searchParamsForQuery, page],
		queryFn: () => getFiles({ ...searchParamsForQuery, page }),
		refetchInterval: (response) => ((response?.data?.counters?.inProcess ?? 0) > 0 ? 6000 : false),
		onSuccess: (response) => {
			const data = response?.data;
			setFilesCounters(data?.counters);
			updateFilesData({ files: data?.results });
		},
		onError: (err) => {
			updateFilesData({ files: null });
			showErrorNotification(err as AxiosError);
		},
	});

	useQuery({
		queryKey: ['get/assertion-sets'],
		queryFn: () => getAssertionsSets(),
		onSuccess: (response) => {
			setAssertionsSetsData(response?.data);
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
	});

	useEffect(() => {
		setFileDetails(null);
		setCustomFileDetails(null);
	}, []);

	const lastCategoryRef = useRef<string | undefined>(
		localStorage.getItem('files-last-category') ?? undefined,
	);
	useEffect(() => {
		if (category && category !== lastCategoryRef.current) {
			lastCategoryRef.current = category;
			localStorage.setItem('files-last-category', category);
			handleToggleAllFiles(true);
			changeSearchParams({
				models: (category !== 'all' ? [{ category }] : []) as SearchParams['models'],
			});
		}
	}, [category]);

	useEffect(() => {
		if (partnerId) {
			changeSearchParams({ ...initialSearch, partnerId });
		}
	}, [partnerId]);

	useEffect(() => {
		if (triggerExport) {
			handleFTPImport('/');
			setTriggerExport(false);
		}
	}, [triggerExport]);

	const handleEditFile = (fileId: string) => {
		if (partnerId) {
			setFilePartnerId(partnerId);
		}
		navigate(`/${Prefixes.editFile}${fileId}`);
	};

	const handleMergeExport = (type: string, ids: string[] = fileIdsforActions, fileId?: string) => {
		mergeMutate(
			{ type, ids, includeAllSplitParts: fileId ?? fileIdForAction ? true : undefined },
			{
				onSuccess: () => {
					handleResetFileIds();
					setDownloadModalIsOpen(false);
					enqueueSnackbar({ message: t('files.snackbar.check_your_email'), variant: 'success' });
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	const handleFTPImport = useCallback(
		(localPath: string, type?: DefaultExportType) => {
			const payloadFileType = type || fileType || company?.defaultExport.defaultType;

			if (payloadFileType) {
				const mutationValues = {
					type: payloadFileType,
					ftpPath: localPath,
					ids: fileIdsforActions,
					isCompanyFtp: true,
				};
				if (fileProcess === ProcessFile.EXPORT) {
					ftpExportMutate(mutationValues, {
						onSuccess: async () => {
							setOpenFtpModal(false);
							handleResetFileIds();
							setDownloadModalIsOpen(false);
							enqueueSnackbar({
								message: t('files.snackbar.success_esport'),
								variant: 'success',
							});
						},
						onError: (err) => {
							showErrorNotification(
								err as AxiosError,
								t('files.snackbar.error_while_uploading'),
							);
						},
					});
				}

				if (fileProcess === ProcessFile.MERGE) {
					mergeFtpMutate(mutationValues, {
						onSuccess: async () => {
							setOpenFtpModal(false);
							handleResetFileIds();
							setDownloadModalIsOpen(false);
							enqueueSnackbar({
								message: t('files.snackbar.success_esport'),
								variant: 'success',
							});
						},
						onError: (err) => {
							showErrorNotification(
								err as AxiosError,
								t('files.snackbar.error_while_uploading'),
							);
						},
					});
				}
			}
		},
		[fileType, fileIdsforActions, fileProcess],
	);

	const handleProcessFile = (process: ProcessFile, fileId?: string) => {
		setFileProcess(process);

		if (fileId) {
			setFileIdForAction(fileId);
		}

		const ids = fileId ? [fileId] : selectedFiles;

		const reviewIssues = fileId
			? files?.find((file) => file._id === fileId)?.reviewIssues ?? []
			: selectedFiles.flatMap((id) => files?.find((file) => file._id === id)?.reviewIssues ?? []);

		if (reviewIssues.length) {
			setRequiresReviewModalOpen(true);
			setReviewParams(reviewIssues);
		} else {
			checkIfExportedHandler(ids, process, fileId);
		}
	};

	const exportToErp = (fileId?: string) => {
		const ids = fileId ? [fileId] : selectedFiles;
		exportEdildeltaErp({ ids });
	};

	const exportToFaterErp = (fileId?: string) => {
		const ids = fileId ? [fileId] : selectedFiles;
		exportFaterErp({ ids });
	};

	const checkIfExportedHandler = (fileIds: string[], process?: ProcessFile, fileId?: string) => {
		checkExportMutate(
			{ ids: fileIds },
			{
				onSuccess: (response) => {
					if (response.data.length) {
						setAlreadyExportedFiles(response.data);
						setAlreadyExportedFilesModalOpen(true);
					} else {
						finalExportHandler(process, fileIds, fileId);
					}
				},
			},
		);
	};

	const finalExportHandler = (
		process: ProcessFile | undefined = fileProcess,
		ids: string[] = fileIdsforActions,
		fileId?: string,
	) => {
		setAlreadyExportedFilesModalOpen(false);
		setAlreadyExportedFiles(undefined);
		if (company?.defaultExport) {
			if (!company?.defaultExport?.defaultType || !company?.defaultExport?.defaultDestination) {
				setDownloadModalIsOpen(true);
			} else if (company?.defaultExport?.defaultDestination === DefaultExportDestination.FTP) {
				setTriggerExport(true);
			} else {
				if (process === ProcessFile.EXPORT) {
					downloadFile({
						fileId: ids,
						type: company?.defaultExport?.defaultType,
					});
				}
				if (process === ProcessFile.MERGE) {
					handleMergeExport(company?.defaultExport?.defaultType, ids, fileId);
				}
			}
		} else {
			setDownloadModalIsOpen(true);
		}
	};

	const handleDeleteFile = useCallback(
		(fileId?: string) => {
			if (!fileId && selectedFiles.length === 0) {
				return enqueueSnackbar({
					message: t('files.snackbar.no_files_to_remove'),
					variant: 'error',
				});
			}
			if (fileId) {
				setFileIdForAction(fileId);
			}
			setDeleteFilesOpen(true);
		},
		[selectedFiles],
	);

	const handleAssertions = (fileId?: string) => {
		if (fileId) {
			setFileIdForAction(fileId);
		}
		setIsAssertionsOpen(true);
	};

	const updateFileAssertionsHandler = (asId?: string) => {
		fileAssertionMutate(
			{
				ids: fileIdsforActions,
				assertionSetsId: !asId ? null : asId,
			},
			{
				onSuccess: async () => {
					setIsAssertionsOpen(false);
					if (asId) {
						enqueueSnackbar({
							message: t('assertions.modal.success_adding'),
							variant: 'success',
						});
					} else {
						enqueueSnackbar({
							message: t('assertions.modal.success_removing'),
							variant: 'success',
						});
					}
					handleResetFileIds();
					await queryClient.refetchQueries({ queryKey: ['get/files'] });
				},
				onError: (err) => {
					showErrorNotification(err as AxiosError);
				},
			},
		);
	};

	const handleResetFileIds = () => {
		resetSelection();
		setFileIdForAction('');
		setFileProcess(undefined);
	};

	if (isLoading && !files?.length) return <Loader />;

	return (
		<PageWrapper>
			<AsModalList
				open={isAssertionsOpen}
				handleApply={updateFileAssertionsHandler}
				handleRemoveAssertions={updateFileAssertionsHandler}
				handleClose={() => {
					setIsAssertionsOpen(false);
					handleResetFileIds();
				}}
				loading={fileAssertionLoading}
				defaultAssertionId={defaultAsertionId}
			/>
			<DeleteFilesModal
				ids={fileIdsforActions}
				open={deleteFilesOpen}
				handleClose={() => {
					handleResetFileIds();
					setDeleteFilesOpen(false);
				}}
			/>
			{!partnerId && (
				<PageHeader
					title={`${t('files.title')} ${
						ReadableCategories[String(category)] ? t(ReadableCategories[String(category)]) : ''
					}`.trim()}
					description={t(`files.description.${searchParams.status}`)}
				/>
			)}
			<FiltersAndSearch
				category={category}
				filesCounters={filesCounters}
				searchParams={searchParams}
				changeSearchParams={changeSearchParams}
				handleToggleAllFiles={handleToggleAllFiles}
				uploaderOptions={uploaderOptions}
			/>
			<FilesDashboard
				data={{
					files: preparedFilesData,
					page: filesResponse?.data.page || 0,
					perPage: filesResponse?.data.perPage || 0,
					totalCount: filesResponse?.data.totalCount || 0,
				}}
				filesCount={files?.length ?? 0}
				selectedFiles={selectedFiles}
				isAllSelected={isAllSelected}
				isIndeterminate={isIndeterminate}
				handleToggleFile={handleToggleFile}
				handleToggleAllFiles={handleToggleAllFiles}
				handleEditFile={handleEditFile}
				handleProcessFile={handleProcessFile}
				handleDeleteFile={handleDeleteFile}
				handleAssertions={handleAssertions}
				onPageChange={(page) => {
					changeSearchParams({ page: `${page}` });
					setPage(page);
				}}
				exportToErp={exportToErp}
				exportToFaterErp={exportToFaterErp}
				searchParams={searchParams}
				changeSearchParams={changeSearchParams}
				uploaderOptions={uploaderOptions}
			/>
			<DownloadModal
				open={downloadModalIsOpen}
				handleClose={() => {
					handleResetFileIds();
					setDownloadModalIsOpen(false);
				}}
				fileId={fileIdsforActions}
				handleFtpOpen={(type) => {
					if (type) {
						handleFTPImport('/', type);
					} else {
						setOpenFtpModal(true);
					}
				}}
				fileType={fileType}
				setFileType={setFileType}
				fileProcess={fileProcess}
				mergeExport={handleMergeExport}
			/>
			<FtpImportFilesModal
				open={openFtpModal}
				handleImport={(localPath) => handleFTPImport(localPath)}
				handleClose={() => setOpenFtpModal(false)}
				exportFtp
				isPending={ftpExportIsLoading || mergeFtpIsLoading}
			/>
			<RequiresReviewModal
				params={reviewParams}
				open={requiresReviewModalOpen}
				handleClose={() => {
					setRequiresReviewModalOpen(false);
					handleResetFileIds();
				}}
				handleExport={() => {
					setRequiresReviewModalOpen(false);
					checkIfExportedHandler(fileIdsforActions, fileProcess, fileIdForAction || undefined);
				}}
			/>
			<AlreadyExportedFiles
				open={alreadyExportedFilesModalOpen}
				ids={alreadyExportedFiles || []}
				handleClose={() => {
					setAlreadyExportedFilesModalOpen(false);
					setAlreadyExportedFiles(undefined);
					handleResetFileIds();
				}}
				handleExport={() =>
					finalExportHandler(fileProcess, fileIdsforActions, fileIdForAction || undefined)
				}
			/>
		</PageWrapper>
	);
};
